import React, { createContext, useContext, PropsWithChildren } from 'react';
import { usePeriodPainReportData } from '@components/dashboard/hooks/usePeriodPainReportData';
import { GeneratedPPCData } from '@components/dashboard/utils/periodPainConsultation';
import { PainReportSection } from '@contracts';
import ConditionsIcon from '@images/vms-account/ppc/conditions_small.svg';
import DealsIcon from '@images/vms-account/ppc/deals_small.svg';
import PainReliefIcon from '@images/vms-account/ppc/routine_small.svg';
import SpecialistsIcon from '@images/vms-account/ppc/specialists_small.svg';
import SummaryIcon from '@images/vms-account/ppc/summary_small.svg';
import scrollToSection from '@utils/scrollToSection';
import useNavigate from '@utils/useNavigate';

export interface PeriodPainReportContextProps {
  loading: boolean;
  locked: boolean;
  noPainReportData: boolean;
  painReportData: GeneratedPPCData | null;
  changeCurrentTab: (options: { sectionId: string; parameterId?: string; scrollSectionId?: string }) => void;
}

const reportTabs = [
  {
    id: PainReportSection.Summary,
    title: 'Summary',
    icon: <SummaryIcon />,
    url: '/dashboard/period-pain-report'
  },
  {
    id: PainReportSection.PainReliefRoutine,
    title: 'Pain relief routine',
    icon: <PainReliefIcon />,
    url: '/dashboard/period-pain-report/my-routine'
  },
  {
    id: PainReportSection.Conditions,
    title: 'Conditions',
    icon: <ConditionsIcon />,
    url: '/dashboard/period-pain-report/conditions'
  },
  {
    id: PainReportSection.Specialists,
    title: 'Specialists',
    icon: <SpecialistsIcon />,
    url: '/dashboard/period-pain-report/book-a-call'
  },
  {
    id: PainReportSection.DealsAndResources,
    title: 'Deals & resources',
    icon: <DealsIcon />,
    url: '/dashboard/period-pain-report/deals-and-resources'
  }
];

const initialContext: PeriodPainReportContextProps = {
  loading: true,
  locked: true,
  noPainReportData: false,
  painReportData: null,
  changeCurrentTab: () => {}
};

export const PeriodPainReportContext = createContext<PeriodPainReportContextProps>(initialContext);

export const PeriodPainReportProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { loading, locked, noPainReportData, painReportData } = usePeriodPainReportData();
  const { navigateWrapper } = useNavigate();

  // DEPRECATED: To be refactored to use anchor links to the section of the page and use only links
  const changeCurrentTab = (options: { sectionId: string; parameterId?: string; scrollSectionId?: string }) => {
    const reportSection = reportTabs.find((tab) => tab.id === options.sectionId);

    if (reportSection) {
      let searchParams = '';

      if (options.parameterId) {
        if (reportSection.id === PainReportSection.DealsAndResources) {
          searchParams += `resourceId=${options.parameterId}`;
        }

        if (reportSection.id === PainReportSection.Conditions) {
          searchParams += `conditionId=${options.parameterId}`;
        }
      }

      navigateWrapper(`${reportSection.url}?${searchParams}`);

      window.scrollTo({ top: 0, behavior: 'smooth' });

      setTimeout(() => {
        if (options.scrollSectionId) {
          scrollToSection({ id: options.scrollSectionId, offset: 130 });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <PeriodPainReportContext.Provider value={{ loading, locked, painReportData, noPainReportData, changeCurrentTab }}>
      {children}
    </PeriodPainReportContext.Provider>
  );
};

export function usePeriodPainReport(): PeriodPainReportContextProps {
  const context = useContext(PeriodPainReportContext);
  if (context === undefined) {
    throw new Error('usePPRConditions must be used within a PPRConditionsProvider');
  }
  return context;
}
